<template>
  <div class="store-page py-3 px-3">
    <div v-if="loadingSeller" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
    </div>

    <div v-else-if="sellerError" class="store-error card border-0 shadow-sm mx-auto">
      <div class="card-body text-center py-5">
        <i class="fas fa-store-slash fa-3x text-muted mb-3"></i>
        <h5 class="mb-2">Tienda no disponible</h5>
        <p class="text-muted mb-0">{{ sellerError }}</p>
      </div>
    </div>

    <template v-else>
      <div class="store-header mb-3">
        <div class="store-brand">
          <div class="store-brand-text">
            <p class="store-brand-label text-uppercase text-muted mb-1">Vendedor</p>
            <h5 class="mb-0">{{ seller.name }}</h5>
            <p class="text-sm text-muted mb-0">Catálogo de productos</p>
          </div>
        </div>
      </div>

      <section class="store-products card border-0 shadow-sm">
        <div class="card-body">
          <div class="store-products-header mb-3">
            <div class="store-products-toolbar">
              <input
                v-model="search"
                type="text"
                class="form-control form-control-sm store-search"
                placeholder="Buscar producto..."
                @input="onSearch"
              />
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary store-filter-toggle mb-0"
                :class="{ 'store-filter-toggle--active': showMobileFilters }"
                @click="showMobileFilters = !showMobileFilters"
              >
                <i class="fas fa-filter"></i>
              </button>
              <div class="store-filter-selects" :class="{ 'store-filter-selects--open': showMobileFilters }">
                <select
                  v-model="categoryFilter"
                  class="form-select form-select-sm store-filter-select"
                  @change="onFilterChange"
                >
                  <option value="">Todas las categorías</option>
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="String(category.id)"
                  >
                    {{ category.name }}
                  </option>
                </select>
                <select
                  v-model="marcaFilter"
                  class="form-select form-select-sm store-filter-select"
                  @change="onFilterChange"
                >
                  <option value="">Todas las marcas</option>
                  <option
                    v-for="marca in marcas"
                    :key="marca.id"
                    :value="String(marca.id)"
                  >
                    {{ marca.name }}
                  </option>
                </select>
                <select
                  v-model="offerFilter"
                  class="form-select form-select-sm store-filter-select"
                  @change="onFilterChange"
                >
                  <option value="all">Todos</option>
                  <option value="offer">En oferta</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="loadingProducts" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
          </div>

          <div v-else class="store-grid">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="store-product-card"
              :disabled="!canAddProduct(product)"
              @click="addToCart(product)"
            >
              <div class="store-product-media">
                <div class="store-product-badges">
                  <span v-if="hasOffer(product)" class="store-offer-badge">
                    Oferta {{ formatDiscountPercent(product) }}%
                  </span>
                  <span class="store-stock-badge" :title="stockBadgeTitle(product)">
                    {{ stockBadgeLabel(product) }}
                  </span>
                </div>
                <img
                  v-if="productImageUrl(product)"
                  :src="productImageUrl(product)"
                  :alt="product.name"
                  class="store-product-img"
                  loading="lazy"
                  @error="onProductImageError($event, product)"
                />
                <div v-else class="store-product-img-placeholder">
                  <i class="fas fa-box-open"></i>
                </div>
              </div>
              <div class="store-product-body">
                <p class="store-product-name">{{ product.name }}</p>
                <p class="store-product-ref">Ref: {{ product.ref || '—' }}</p>
                <div class="store-product-prices">
                  <template v-if="hasOffer(product)">
                    <span class="store-price-normal">{{ formatCurrency(getNormalPrice(product)) }}</span>
                    <span class="store-price-discount">{{ formatCurrency(getDiscountedUnitPrice(product)) }}</span>
                    <p v-if="shouldShowMinUnitsMessage(product)" class="store-offer-note">
                      {{ offerMinUnitsMessage(product) }}
                    </p>
                  </template>
                  <template v-else>
                    <span class="store-price-discount">{{ formatCurrency(getNormalPrice(product)) }}</span>
                  </template>
                </div>
              </div>
            </button>
          </div>

          <div
            v-if="!loadingProducts && products.length === 0"
            class="text-center text-muted py-4"
          >
            {{ emptyProductsMessage }}
          </div>

          <div class="store-pagination-footer mt-3 pt-2 border-top">
            <span class="text-sm text-muted store-pagination-info">
              Mostrando {{ pagination.from || 0 }}-{{ pagination.to || 0 }} de {{ pagination.total || 0 }}
            </span>
            <div class="store-pagination">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="pagination.currentPage <= 1"
                @click="changePage(pagination.currentPage - 1)"
              >
                &lt;
              </button>
              <span class="store-pagination-label">
                {{ pagination.currentPage }} / {{ pagination.lastPage || 1 }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="pagination.currentPage >= pagination.lastPage"
                @click="changePage(pagination.currentPage + 1)"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </section>

      <button
        type="button"
        class="store-cart-fab"
        :class="{ 'store-cart-fab--bounce': floatingBarBounce }"
        aria-label="Ver carrito"
        @click="showCheckout = true"
      >
        <i class="fas fa-shopping-cart"></i>
        <span v-if="cartCount > 0" class="store-cart-fab-badge">{{ cartCount }}</span>
      </button>

      <transition name="store-backdrop-fade">
        <div v-if="showCheckout" class="store-checkout-backdrop" @click.self="closeCheckout">
          <transition name="store-panel-slide" appear>
            <div class="store-checkout card border-0 shadow-lg" @click.stop>
              <div class="card-header d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Tu pedido</h6>
                <button type="button" class="btn-close" @click="closeCheckout"></button>
              </div>
              <div class="card-body">
            <div v-if="cart.length === 0" class="text-muted text-sm">No hay productos seleccionados.</div>
            <div v-else class="store-cart-items">
              <div
                v-for="(item, index) in cart"
                :key="item.inventory_id"
                class="store-cart-card"
              >
                <div class="store-cart-card-header">
                  <div class="store-cart-card-title">
                    <span
                      v-if="cartItemHasOffer(item)"
                      class="store-offer-badge store-offer-badge--cart"
                    >
                      Oferta {{ formatDiscountPercent(item) }}%
                    </span>
                    <span class="store-cart-card-name">{{ item.name }}</span>
                  </div>
                  <button
                    type="button"
                    class="store-cart-card-remove"
                    aria-label="Quitar producto"
                    @click="removeFromCart(index)"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <div class="store-cart-card-qty-row">
                  <div class="store-cart-card-qty">
                    <button type="button" class="store-qty-btn" @click="changeQty(index, -1)">−</button>
                    <input
                      type="text"
                      class="store-qty-value"
                      inputmode="numeric"
                      :value="item.quantity"
                      @change="onQtyInput(index, $event)"
                      @focus="$event.target.select()"
                    />
                    <button type="button" class="store-qty-btn" @click="changeQty(index, 1)">+</button>
                  </div>
                </div>

                <div class="store-cart-card-prices">
                  <div class="store-cart-price-field">
                    <template v-if="cartItemHasOffer(item)">
                      <template v-if="discountAppliesToItem(item)">
                        <span class="store-cart-price-value store-cart-price-value--normal">{{ formatCurrency(item.normal_unit_price) }}</span>
                        <span class="store-cart-price-value store-cart-price-value--discount">{{ formatCurrency(getEffectiveUnitPrice(item)) }}</span>
                      </template>
                      <template v-else>
                        <span class="store-cart-price-value">{{ formatCurrency(item.normal_unit_price) }}</span>
                      </template>
                      <p v-if="shouldShowMinUnitsMessage(item)" class="store-offer-note store-offer-note--cart mb-0 mt-1">
                        {{ offerMinUnitsMessage(item) }}
                      </p>
                    </template>
                    <template v-else>
                      <span class="store-cart-price-value">{{ formatCurrency(item.normal_unit_price) }}</span>
                    </template>
                  </div>
                  <div class="text-end">
                    <span class="store-cart-price-total">{{ formatCurrency(lineTotal(item)) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="cart.length > 0" class="store-cart-summary border-top pt-3 mt-3">
              <div class="d-flex justify-content-between mb-3">
                <span class="fw-bold">Total</span>
                <span class="fw-bold text-success">{{ formatCurrency(cartTotal) }}</span>
              </div>

              <div class="store-contact-form border-top pt-3 mt-1">
                <p class="fw-semibold text-sm mb-2">Datos de contacto</p>

                <div class="store-delivery-options mb-3">
                  <label
                    class="store-delivery-option"
                    :class="{ 'store-delivery-option--active': orderForm.deliveryType === 'domicilio' }"
                    @click="orderForm.deliveryType = 'domicilio'"
                  >
                    <span class="store-delivery-option-icon"><i class="fas fa-truck"></i></span>
                    <span class="store-delivery-option-label">Domicilio</span>
                    <span class="store-delivery-option-radio">
                      <span v-if="orderForm.deliveryType === 'domicilio'" class="store-delivery-radio-dot"></span>
                    </span>
                  </label>
                  <label
                    class="store-delivery-option"
                    :class="{ 'store-delivery-option--active': orderForm.deliveryType === 'recoger' }"
                    @click="orderForm.deliveryType = 'recoger'"
                  >
                    <span class="store-delivery-option-icon"><i class="fas fa-shopping-bag"></i></span>
                    <span class="store-delivery-option-label">Para llevar / Recoger</span>
                    <span class="store-delivery-option-radio">
                      <span v-if="orderForm.deliveryType === 'recoger'" class="store-delivery-radio-dot"></span>
                    </span>
                  </label>
                </div>

                <div class="mb-2">
                  <label class="form-label text-sm mb-1">Nombre <span class="text-danger">*</span></label>
                  <input
                    v-model.trim="orderForm.nombre"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Tu nombre"
                  />
                </div>

                <div class="mb-2">
                  <label class="form-label text-sm mb-1">Celular <span class="text-danger">*</span></label>
                  <input
                    v-model.trim="orderForm.celular"
                    type="tel"
                    class="form-control form-control-sm"
                    placeholder="Número de celular"
                  />
                </div>

                <div v-if="orderForm.deliveryType === 'domicilio'" class="mb-2">
                  <label class="form-label text-sm mb-1">Dirección <span class="text-danger">*</span></label>
                  <input
                    v-model.trim="orderForm.direccion"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Escribe tu dirección"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label text-sm mb-1">Comentarios de la orden</label>
                  <textarea
                    v-model.trim="orderForm.comentarios"
                    class="form-control form-control-sm"
                    rows="2"
                    placeholder="Instrucciones adicionales..."
                  ></textarea>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-success w-100"
                :disabled="!seller.phone || cart.length === 0"
                @click="sendWhatsAppOrder"
              >
                <i class="fab fa-whatsapp me-1"></i> Hacer pedido
              </button>
              <p v-if="!seller.phone" class="text-danger text-sm mt-2 mb-0">
                El vendedor no tiene teléfono registrado.
              </p>
            </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import storeService from "@/services/store.service";
import Swal from "sweetalert2";
import logoDefault from "@/assets/img/icon.png";
import { playCartAddSound, closeCartAddSound } from "@/utils/cartAddSound";

export default {
  name: "PublicStore",
  data() {
    return {
      sellerToken: "",
      seller: { name: "", phone: "", logo_url: null, company_name: "" },
      sellerError: "",
      loadingSeller: true,
      products: [],
      loadingProducts: false,
      search: "",
      showMobileFilters: false,
      offerFilter: "all",
      categoryFilter: "",
      marcaFilter: "",
      categories: [],
      marcas: [],
      searchTimer: null,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        from: 0,
        to: 0,
      },
      cart: [],
      showCheckout: false,
      floatingBarBounce: false,
      orderForm: {
        deliveryType: "domicilio",
        nombre: "",
        celular: "",
        direccion: "",
        comentarios: "",
      },
      brokenImages: {},
      logoBroken: false,
    };
  },
  computed: {
    storeLogoUrl() {
      if (this.logoBroken || !this.seller.logo_url) {
        return logoDefault;
      }
      return this.seller.logo_url;
    },
    cartCount() {
      return this.cart.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    },
    cartTotal() {
      return this.cart.reduce((sum, item) => sum + this.lineTotal(item), 0);
    },
    emptyProductsMessage() {
      if (this.offerFilter === "offer") {
        return "No hay productos en oferta";
      }
      if (this.categoryFilter || this.marcaFilter) {
        return "No hay productos con los filtros seleccionados";
      }
      return "No hay productos disponibles";
    },
  },
  methods: {
    async initStore() {
      this.sellerToken = String(this.$route.query.seller || "").trim();
      this.logoBroken = false;
      if (!this.sellerToken) {
        this.sellerError = "Enlace inválido. Falta el identificador del vendedor.";
        this.loadingSeller = false;
        return;
      }

      try {
        const res = await storeService.getSeller(this.sellerToken);
        this.seller = res.data || {};
        this.loadingSeller = false;
        await Promise.all([this.loadFilters(), this.loadProducts(1)]);
      } catch (error) {
        this.sellerError =
          error?.response?.data?.message || "No se pudo cargar la tienda del vendedor.";
        this.loadingSeller = false;
      }
    },
    async loadFilters() {
      try {
        const res = await storeService.getFilters(this.sellerToken);
        this.categories = Array.isArray(res.data?.categories) ? res.data.categories : [];
        this.marcas = Array.isArray(res.data?.marcas) ? res.data.marcas : [];
      } catch (error) {
        console.error("No se pudieron cargar los filtros de la tienda:", error);
        this.categories = [];
        this.marcas = [];
      }
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const res = await storeService.getProducts(this.sellerToken, {
          page,
          per_page: 12,
          search: this.search || undefined,
          on_offer: this.offerFilter === "offer" ? 1 : undefined,
          category_id: this.categoryFilter || undefined,
          marca_id: this.marcaFilter || undefined,
        });
        const data = res.data || {};
        this.products = Array.isArray(data.data) ? data.data : [];
        this.pagination = {
          currentPage: data.current_page || 1,
          lastPage: data.last_page || 1,
          total: data.total || 0,
          from: data.from || 0,
          to: data.to || 0,
        };
      } catch (error) {
        this.products = [];
        Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudieron cargar los productos.",
          "error"
        );
      } finally {
        this.loadingProducts = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 350);
    },
    onFilterChange() {
      this.loadProducts(1);
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.loadProducts(page);
    },
    getNormalPrice(product) {
      if (product?.precio1 !== undefined && product?.precio1 !== null) {
        const fromCatalog = parseFloat(product.precio1);
        if (!Number.isNaN(fromCatalog)) {
          return fromCatalog;
        }
      }
      const fromCart = parseFloat(product?.normal_unit_price ?? product?.unit_price);
      return Number.isNaN(fromCart) ? 0 : fromCart;
    },
    formatDiscountPercent(product) {
      const discount = parseFloat(product?.descuento);
      if (Number.isNaN(discount) || discount <= 0) {
        return 0;
      }
      return Number.isInteger(discount) ? discount : discount.toFixed(2).replace(/\.?0+$/, "");
    },
    getDiscountPercent(product) {
      const discount = parseFloat(product?.descuento);
      return Number.isNaN(discount) || discount <= 0 ? 0 : discount;
    },
    getMinUnitsForDiscount(product) {
      const raw = product?.descuento_min_unidades ?? product?.descuentoMinUnidades;
      const minUnits = parseInt(String(raw ?? "").trim(), 10);
      return Number.isNaN(minUnits) || minUnits < 1 ? 1 : minUnits;
    },
    shouldShowMinUnitsMessage(product) {
      return this.getDiscountPercent(product) > 0 && this.getMinUnitsForDiscount(product) > 1;
    },
    discountAppliesToItem(item) {
      if (!this.cartItemHasOffer(item)) {
        return false;
      }
      const qty = parseFloat(item.quantity) || 0;
      return qty >= this.getMinUnitsForDiscount(item);
    },
    hasOffer(product) {
      return this.getDiscountPercent(product) > 0;
    },
    cartItemHasOffer(item) {
      return this.getDiscountPercent(item) > 0;
    },
    getDiscountedUnitPrice(product) {
      const normal = this.getNormalPrice(product);
      const discount = this.getDiscountPercent(product);
      if (discount <= 0) {
        return normal;
      }
      return normal * (1 - discount / 100);
    },
    getEffectiveUnitPrice(item) {
      const normal = parseFloat(item.normal_unit_price ?? item.unit_price) || 0;
      if (!this.cartItemHasOffer(item)) {
        return normal;
      }
      const qty = parseFloat(item.quantity) || 0;
      if (qty >= this.getMinUnitsForDiscount(item)) {
        return this.getDiscountedUnitPrice(item);
      }
      return normal;
    },
    offerMinUnitsMessage(product) {
      if (!this.shouldShowMinUnitsMessage(product)) {
        return "";
      }
      const minUnits = this.getMinUnitsForDiscount(product);
      return `A partir de ${minUnits} unidades`;
    },
    stockBadgeLabel(product) {
      if (!product.manage_stock) {
        return "stock: ∞";
      }
      const qty = Math.floor(product.quantity || 0);
      return qty > 0 ? `Stock: ${qty}` : "Agotado";
    },
    stockBadgeTitle(product) {
      if (!product.manage_stock) {
        return "Producto siempre disponible";
      }
      const qty = Math.floor(product.quantity || 0);
      return qty > 0 ? `${qty} unidad(es) disponibles` : "Sin unidades disponibles";
    },
    productImageUrl(product) {
      if (this.brokenImages[product.id]) return null;
      return product.image_url || null;
    },
    onProductImageError(_event, product) {
      this.brokenImages = { ...this.brokenImages, [product.id]: true };
    },
    onLogoError() {
      this.logoBroken = true;
    },
    canAddProduct(product) {
      if (!product.manage_stock) return true;
      return (product.quantity || 0) > 0;
    },
    maxQuantity(product) {
      if (!product.manage_stock) return 9999;
      return Math.max(1, Math.floor(product.quantity || 0));
    },
    addToCart(product) {
      if (!this.canAddProduct(product)) {
        Swal.fire("Sin stock", "No hay unidades disponibles de este producto.", "warning");
        return;
      }

      const existing = this.cart.find((item) => item.inventory_id === product.id);
      if (existing) {
        const nextQty = (parseFloat(existing.quantity) || 0) + 1;
        if (product.manage_stock && nextQty > existing.max_quantity) {
          Swal.fire("Stock limitado", `Solo hay ${existing.max_quantity} unidad(es) disponibles.`, "warning");
          return;
        }
        existing.quantity = nextQty;
        existing.normal_unit_price = this.getNormalPrice(product);
        existing.descuento = this.getDiscountPercent(product);
        existing.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      } else {
        const normalPrice = this.getNormalPrice(product);
        this.cart.push({
          inventory_id: product.id,
          name: product.name,
          ref: product.ref,
          normal_unit_price: normalPrice,
          unit_price: normalPrice,
          descuento: this.getDiscountPercent(product),
          descuento_min_unidades: this.getMinUnitsForDiscount(product),
          quantity: 1,
          max_quantity: this.maxQuantity(product),
        });
      }

      this.triggerCartAddedFeedback();
    },
    triggerCartAddedFeedback() {
      playCartAddSound();
      this.floatingBarBounce = false;
      this.$nextTick(() => {
        this.floatingBarBounce = true;
        if (this._bounceTimer) {
          clearTimeout(this._bounceTimer);
        }
        this._bounceTimer = setTimeout(() => {
          this.floatingBarBounce = false;
        }, 480);
      });
    },
    onQtyInput(index, event) {
      const item = this.cart[index];
      let val = parseFloat(event.target.value);
      if (isNaN(val) || val < 1) {
        val = 1;
      }
      if (item.max_quantity && val > item.max_quantity) {
        val = item.max_quantity;
        Swal.fire("Stock limitado", `Solo hay ${item.max_quantity} unidad(es) disponibles.`, "warning");
      }
      const product = this.products.find((p) => p.id === item.inventory_id);
      if (product) {
        item.normal_unit_price = this.getNormalPrice(product);
        item.descuento = this.getDiscountPercent(product);
        item.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      }
      item.quantity = val;
      event.target.value = val;
    },
    changeQty(index, delta) {
      const item = this.cart[index];
      const next = (parseFloat(item.quantity) || 0) + delta;
      if (next < 1) {
        this.removeFromCart(index);
        return;
      }
      if (item.max_quantity && next > item.max_quantity) {
        Swal.fire("Stock limitado", `Solo hay ${item.max_quantity} unidad(es) disponibles.`, "warning");
        return;
      }
      const product = this.products.find((p) => p.id === item.inventory_id);
      if (product) {
        item.normal_unit_price = this.getNormalPrice(product);
        item.descuento = this.getDiscountPercent(product);
        item.descuento_min_unidades = this.getMinUnitsForDiscount(product);
      }
      item.quantity = next;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
      if (this.cart.length === 0) {
        this.showCheckout = false;
      }
    },
    lineTotal(item) {
      const qty = parseFloat(item.quantity) || 0;
      return qty * this.getEffectiveUnitPrice(item);
    },
    formatCurrency(value) {
      return `$ ${parseFloat(value || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    closeCheckout() {
      this.showCheckout = false;
      this.orderForm = {
        deliveryType: "domicilio",
        nombre: "",
        celular: "",
        direccion: "",
        comentarios: "",
      };
    },
    normalizePhone(phone) {
      const digits = String(phone || "").replace(/\D/g, "");
      if (!digits) return "";
      if (digits.length === 10) {
        return `57${digits}`;
      }
      return digits;
    },
    buildWhatsAppMessage() {
      const productLines = this.cart.map((item) => {
        const qty = parseFloat(item.quantity) || 0;
        const total = this.lineTotal(item);
        const unitPrice = this.getEffectiveUnitPrice(item);
        const offerNote = this.cartItemHasOffer(item)
          ? ` (${this.offerMinUnitsMessage(item)}, ${this.formatCurrency(unitPrice)}/u)`
          : "";
        return `- ${item.name} (Ref: ${item.ref || "—"}) x${qty} = ${this.formatCurrency(total)}${offerNote}`;
      });

      const deliveryLabel =
        this.orderForm.deliveryType === "domicilio" ? "Domicilio" : "Para llevar / Recoger";

      const lines = [
        "*Nuevo pedido desde tienda*",
        "",
        `Vendedor: ${this.seller.name}`,
        "",
        "*Datos del cliente:*",
        `Nombre: ${this.orderForm.nombre}`,
        `Celular: ${this.orderForm.celular}`,
        `Entrega: ${deliveryLabel}`,
      ];

      if (this.orderForm.deliveryType === "domicilio" && this.orderForm.direccion) {
        lines.push(`Dirección: ${this.orderForm.direccion}`);
      }

      if (this.orderForm.comentarios) {
        lines.push(`Comentarios: ${this.orderForm.comentarios}`);
      }

      lines.push(
        "",
        "*Productos:*",
        "",
        productLines.join("\n\n"),
        "",
        `*Total: ${this.formatCurrency(this.cartTotal)}*`
      );

      return lines.join("\r\n");
    },
    sendWhatsAppOrder() {
      const phone = this.normalizePhone(this.seller.phone);
      if (!phone) {
        Swal.fire("Sin teléfono", "El vendedor no tiene WhatsApp configurado.", "warning");
        return;
      }

      if (!this.orderForm.nombre) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu nombre.", "warning");
        return;
      }
      if (!this.orderForm.celular) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu número de celular.", "warning");
        return;
      }
      if (this.orderForm.deliveryType === "domicilio" && !this.orderForm.direccion) {
        Swal.fire("Datos incompletos", "Por favor ingresa tu dirección de entrega.", "warning");
        return;
      }

      const text = encodeURIComponent(this.buildWhatsAppMessage());
      window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener,noreferrer");
    },
  },
  mounted() {
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    this.$store.state.hideConfigButton = true;
    this.initStore();
  },
  beforeUnmount() {
    if (this._bounceTimer) {
      clearTimeout(this._bounceTimer);
    }
    closeCartAddSound();
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    this.$store.state.hideConfigButton = false;
  },
  watch: {
    "$route.query.seller"(value) {
      if (value && value !== this.sellerToken) {
        this.cart = [];
        this.showCheckout = false;
        this.initStore();
      }
    },
  },
};
</script>

<style scoped>
.store-page {
  max-width: 1100px;
  margin: 0 auto;
  min-height: 100vh;
  padding-bottom: 5rem;
}

.store-error {
  max-width: 480px;
}

.store-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.store-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.store-logo {
  max-height: 52px;
  max-width: 160px;
  width: auto;
  object-fit: contain;
}

.store-brand-text {
  width: 100%;
}

.store-brand-label {
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.store-header-action {
  width: 100%;
  max-width: 280px;
}

@media (min-width: 576px) {
  .store-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-align: left;
  }

  .store-brand {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    width: auto;
  }

  .store-brand-text {
    width: auto;
    text-align: left;
  }

  .store-header-action {
    width: auto;
    max-width: none;
    flex-shrink: 0;
  }
}

.store-products-header {
  width: 100%;
}

.store-products-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.store-filter-toggle {
  display: none;
  flex: 0 0 auto;
  width: 31px;
  height: 31px;
  padding: 0;
  font-size: 0.75rem;
  line-height: 1;
  align-items: center;
  justify-content: center;
}

.store-filter-toggle--active {
  background: #344767;
  color: #fff;
  border-color: #344767;
}

.store-filter-selects {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  .store-products-toolbar {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  .store-filter-toggle {
    display: inline-flex;
    flex: 0 0 auto;
  }

  .store-search {
    flex: 1 1 0%;
    width: 100%;
    min-width: 0;
  }

  .store-filter-selects {
    width: 100%;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.25s ease;
  }

  .store-filter-selects--open {
    max-height: 300px;
  }
}

@media (min-width: 576px) {
  .store-products-toolbar {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .store-filter-selects {
    flex-direction: row;
  }
}

.store-search {
  width: 100%;
  min-width: 0;
}

@media (min-width: 576px) {
  .store-search {
    flex: 1 1 auto;
    width: auto;
    min-width: 160px;
  }
}

.store-filter-select {
  width: 100%;
  min-width: 0;
}

@media (min-width: 576px) {
  .store-filter-select {
    width: auto;
    min-width: 130px;
    flex: 0 0 auto;
  }
}

.store-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
}

.store-product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  min-height: 260px;
  border: 2px solid #0b2f7b;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.store-product-card:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(11, 47, 123, 0.15);
}

.store-product-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.store-product-media {
  position: relative;
  flex: 1 1 90%;
  width: 100%;
  min-height: 120px;
  overflow: hidden;
  background: #f0f2f5;
}

.store-product-badges {
  position: absolute;
  top: 0.45rem;
  left: 0.45rem;
  right: 0.45rem;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.35rem;
  pointer-events: none;
}

.store-offer-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  color: #fff;
  background: #fb6340;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.store-offer-badge--cart {
  display: inline-flex;
  margin-right: 0.35rem;
  font-size: 0.62rem;
  padding: 0.28rem 0.55rem;
  flex-shrink: 0;
}

.store-stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: rgba(11, 47, 123, 0.92);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.store-product-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.store-product-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #adb5bd;
  font-size: 2rem;
}

.store-product-body {
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0.65rem 0.75rem;
}

.store-product-name {
  font-size: 0.85rem;
  font-weight: 700;
  text-align: center;
  margin: 0.25rem 0 0;
  line-height: 1.2;
}

.store-product-ref {
  font-size: 0.7rem;
  color: #8898aa;
  margin: 0.15rem 0 0.5rem;
}

.store-product-prices {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  margin-top: auto;
  width: 100%;
}

.store-price-normal {
  font-size: 0.75rem;
  color: #8898aa;
  text-decoration: line-through;
  margin-bottom: 0.15rem;
}

.store-price-discount {
  display: inline-block;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  background: #0b2f7b;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
}

.store-offer-note {
  font-size: 0.62rem;
  color: #fb6340;
  font-weight: 600;
  text-align: center;
  margin: 0.25rem 0 0;
  line-height: 1.2;
}

.store-offer-note--cart {
  text-align: left;
}

.store-pagination-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
}

@media (min-width: 576px) {
  .store-pagination-footer {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-align: left;
  }
}

.store-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.store-pagination-label {
  font-size: 0.875rem;
  min-width: 3rem;
  text-align: center;
}

@media (max-width: 575.98px) {
  .store-grid {
    align-items: start;
  }

  .store-product-card {
    min-height: auto;
  }

  .store-product-media {
    flex: 0 0 auto;
    aspect-ratio: 1 / 1;
    min-height: 0;
  }

  .store-product-body {
    flex: 0 0 auto;
  }

  .store-product-prices {
    margin-top: 0;
  }
}

.store-cart-fab {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 1040;
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: #2dce89;
  color: #fff;
  font-size: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.store-cart-fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 999px;
  background: #dc3545;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

.store-cart-fab--bounce {
  animation: storeCartFabBounce 0.45s ease;
}

@keyframes storeCartFabBounce {
  0%,
  100% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.18);
  }
  55% {
    transform: scale(0.94);
  }
  75% {
    transform: scale(1.06);
  }
}

.store-checkout-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}

.store-checkout {
  width: 100%;
  max-width: 420px;
  height: 100%;
  max-height: 100%;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.store-checkout .card-body {
  flex: 1;
  overflow-y: auto;
}

.store-backdrop-fade-enter-active,
.store-backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}

.store-backdrop-fade-enter-from,
.store-backdrop-fade-leave-to {
  opacity: 0;
}

.store-panel-slide-enter-active,
.store-panel-slide-leave-active {
  transition: transform 0.3s ease;
}

.store-panel-slide-enter-from,
.store-panel-slide-leave-to {
  transform: translateX(100%);
}

.store-cart-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.store-cart-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
}

.store-cart-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.store-cart-card-title {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
}

.store-cart-card-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  text-transform: uppercase;
  line-height: 1.25;
  flex: 1;
  min-width: 0;
}

.store-cart-card-remove {
  border: none;
  background: transparent;
  color: #ea0606;
  padding: 0;
  line-height: 1;
  font-size: 0.85rem;
  cursor: pointer;
}

.store-cart-card-qty-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.store-cart-card-qty {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.store-qty-btn {
  border: none;
  background: #fff;
  color: #344767;
  width: 2rem;
  font-size: 1rem;
  line-height: 1;
  padding: 0.35rem 0;
  cursor: pointer;
}

.store-qty-btn:not(:last-child) {
  border-right: 1px solid #dee2e6;
}

.store-qty-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  width: 4.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  border: none;
  border-right: 1px solid #dee2e6;
  background: #fff;
  text-align: center;
  padding: 0;
  outline: none;
}

.store-cart-card-prices {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
}

.store-cart-price-field {
  flex: 1;
  min-width: 0;
}

.store-cart-price-value {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
}

.store-cart-price-value--normal {
  text-decoration: line-through;
  color: #8898aa;
  font-weight: 500;
}

.store-cart-price-value--discount {
  color: #0b2f7b;
  font-weight: 700;
}

.store-cart-price-total {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #0b2f7b;
}

.store-contact-form {
  .form-label {
    font-weight: 500;
    color: #344767;
  }
}

.store-delivery-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.store-delivery-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.85rem;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s;
  user-select: none;
  background: #fff;

  &.store-delivery-option--active {
    border-color: #2dce89;
  }
}

.store-delivery-option-icon {
  font-size: 1.1rem;
  color: #344767;
  flex-shrink: 0;
}

.store-delivery-option-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
}

.store-delivery-option-radio {
  width: 1.1rem;
  height: 1.1rem;
  border: 2px solid #dee2e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  transition: border-color 0.15s;

  .store-delivery-option--active & {
    border-color: #2dce89;
  }
}

.store-delivery-radio-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: #2dce89;
}
</style>
